/**
 * TestResultInsightCard
 *
 * Glass card surfacing a single test result on the home page. Layout:
 *   [ Test name + state chip           2.8 ]    >
 *   [ One-line testPurpose subtitle   ng/mL ]
 *
 * The first-level card shows the patient's data — value + unit —
 * without an interpretive category label, range-bar zone, or
 * threshold-source line. Category interpretation, reference ranges,
 * cited sources, and the clinical disclaimer live in the tapped
 * detail sheet where they can be presented with context. This
 * matches product direction: data first on overview surfaces,
 * clinical framing on deep-dive surfaces.
 *
 * See issue #1593 for the IA rationale.
 */

import { View, Text, Pressable, Image } from "react-native";
import { CalendarClock, ChevronRight } from "lucide-react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  CardSubtitleText,
  PrimaryTitleText,
  Tag,
  cardSurface,
} from "@louise/moon-ui";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { ResultInterpretation } from "../intelligence/useTestResultInsights";
import { PartnerAvatarTag } from "./PartnerAvatarTag";

const testTubesIcon =
  require("@src/apps/moon/assets/images/you-icons/test-tubes.png") as number;

type Props = {
  interpretation: ResultInterpretation;
  state?: TestResultInsightCardState;
  partnerName?: string;
  /** When provided, tapping the card calls this. Wire to the biomarker
   *  info sheet (A.1c). v0 leaves it undefined so the chevron signals
   *  the affordance without dialing into an unbuilt sheet. */
  onPress?: () => void;
};

export type TestResultInsightCardState =
  | {
      kind: "new-result";
      label?: string;
    }
  | {
      kind: "waiting";
      label?: string;
      expectedLabel?: string;
    }
  | {
      kind: "upcoming";
      label?: string;
      scheduledLabel?: string;
      /** Prefer scheduledLabel for new callers. Kept so older preview
       *  states do not need to be migrated in lockstep. */
      expectedLabel?: string;
      prepLabel?: string;
    };

// Smooth red → amber → green → amber → red gradient. Five stops give
// the bell-curve feel of a "healthy range in the middle" without hard
// segment boundaries.
const RANGE_GRADIENT = [
  "#D88560",
  "#E0A968",
  "#7BAF85",
  "#E0A968",
  "#D88560",
] as const;

// Marker position by category. `as const` keeps the percent strings as
// literal types so they satisfy React Native's DimensionValue.
const CATEGORY_POSITION = {
  low: "15%",
  borderline: "30%",
  normal: "50%",
  high: "85%",
} as const;

const BAR_WIDTH = 64;
const VALUE_LABEL_WIDTH = 56;

function formatScheduledStatusLabel(
  label: string | undefined,
  scheduledWord: string,
  scheduledWithLabel: (trimmed: string) => string,
) {
  if (!label) return scheduledWord;
  const trimmed = label.trim();
  if (!trimmed) return scheduledWord;
  if (/^scheduled\b/i.test(trimmed)) {
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }
  return scheduledWithLabel(trimmed);
}

export function RangeBarWithValue({
  category,
  value,
  unit,
  showValue,
  pending,
  pendingOpacity = 1,
}: {
  /** Null when nothing can place the value: a neutral bar, no marker. */
  category: ResultInterpretation["category"];
  value: string;
  unit: string;
  showValue: boolean;
  pending?: boolean;
  pendingOpacity?: number;
}) {
  const placed = !pending && category !== null;
  const left = category
    ? CATEGORY_POSITION[category]
    : CATEGORY_POSITION.normal;
  return (
    <View
      style={{
        width: BAR_WIDTH,
        paddingTop: showValue ? 18 : 0,
        marginRight: 12,
      }}
    >
      <View style={{ height: 14, justifyContent: "center" }}>
        <LinearGradient
          colors={placed ? RANGE_GRADIENT : ["#D8CDC4", "#BDAEA2", "#D8CDC4"]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          locations={placed ? [0, 0.2, 0.5, 0.8, 1] : [0, 0.5, 1]}
          style={{
            height: 4,
            borderRadius: 2,
            opacity: pending ? pendingOpacity : 1,
          }}
        />
        {/* Vertical marker line at the user's position */}
        {placed ? (
          <View
            style={{
              position: "absolute",
              left,
              top: 0,
              width: 2,
              height: 14,
              borderRadius: 1,
              backgroundColor: semanticColors.text.primary,
              marginLeft: -1,
            }}
          />
        ) : null}
        {/* Value floating above the marker, centered around it. The
            label is fixed-width and offset by half its width so it
            anchors to the marker visually. */}
        {showValue ? (
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              top: -18,
              left,
              width: VALUE_LABEL_WIDTH,
              marginLeft: -(VALUE_LABEL_WIDTH / 2),
              alignItems: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: semanticColors.text.primary,
              }}
            >
              {value}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 9,
                  fontWeight: "500",
                  color: semanticColors.text.tertiary,
                }}
              >
                {" "}
                {unit}
              </Text>
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

export default function TestResultInsightCard({
  interpretation,
  state = { kind: "new-result" },
  partnerName,
  onPress,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestResultInsightCard");
  // Card is presentation-only: the sheet mount lives in the app-level
  // caller (wrapper in `src/apps/moon`) because
  // `LabResultDetailSheet` lives in apps/ and this file is in packages/
  // — cross-boundary import is forbidden. Callers on Home use
  // `TestResultInsightCardWithDetail` which handles the tap + sheet
  // for them. Callers wanting a different tap target can still pass
  // `onPress` directly to this component.
  const isResult = state.kind === "new-result";
  const showValue = isResult && !interpretation.valueInDetails;
  const pendingOpacity = state.kind === "upcoming" ? 0.2 : 1;
  const scheduledLabel =
    state.kind === "upcoming"
      ? (state.scheduledLabel ?? state.expectedLabel)
      : undefined;
  const stateLabel =
    state.label ??
    (state.kind === "waiting"
      ? t("homepage.test_result_insight_card.waiting_for_results")
      : state.kind === "upcoming"
        ? formatScheduledStatusLabel(
            scheduledLabel,
            t("homepage.test_result_insight_card.scheduled"),
            (trimmed) =>
              t("homepage.test_result_insight_card.scheduled_trimmed", {
                params: { trimmed },
              }),
          )
        : t("homepage.test_result_insight_card.new_result"));
  const subtitle =
    state.kind === "waiting" && state.expectedLabel
      ? t("homepage.test_result_insight_card.purpose_with_expected", {
          params: {
            testPurpose: interpretation.testPurpose,
            expectedLabel: state.expectedLabel,
          },
        })
      : interpretation.testPurpose;

  return (
    <Pressable
      onPress={
        onPress
          ? () => {
              capturePress(pressLabel(stateLabel), "test_result_insight_card");
              onPress();
            }
          : undefined
      }
      disabled={!onPress}
      style={{
        ...cardSurface(),
        overflow: "hidden",
      }}
    >
      <View
        className="flex-row items-center"
        style={{
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 12,
        }}
      >
        {/* Test-tubes 3D icon in the canonical row icon slot, matching
            the form-icon position on MedRow / SupplementRow. Renders in
            its native amber. A brown rendering of the asset is the
            proper fix if we want brown without flattening the 3D depth
            (tintColor strips the highlights). */}
        <Image
          source={testTubesIcon}
          style={{ width: 32, height: 32 }}
          resizeMode="contain"
        />

        {/* Middle column: test name + New result chip in the title row,
            testPurpose one-liner as the subtitle. */}
        <View style={{ flex: 1 }}>
          <View className="flex-row items-center" style={{ gap: 8 }}>
            <PrimaryTitleText>{interpretation.testName}</PrimaryTitleText>
            <Tag
              label={stateLabel}
              icon={state.kind === "upcoming" ? CalendarClock : undefined}
            />
          </View>
          <CardSubtitleText>{subtitle}</CardSubtitleText>
          {state.kind === "upcoming" && state.prepLabel ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: semanticColors.text.secondary,
                marginTop: 2,
                lineHeight: 14,
                fontWeight: "700",
              }}
            >
              {state.prepLabel}
            </Text>
          ) : null}
        </View>

        {/* Keep the original data-first range visualization: the measured
            value remains anchored above the marker. The bar has no Low /
            Normal / High label; category only positions the marker. */}
        <RangeBarWithValue
          category={interpretation.category}
          value={interpretation.value}
          unit={interpretation.unit}
          showValue={showValue}
          pending={!isResult}
          pendingOpacity={pendingOpacity}
        />

        {partnerName ? (
          <PartnerAvatarTag label={partnerName} />
        ) : (
          <ChevronRight
            size={14}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
        )}
      </View>
    </Pressable>
  );
}
